import type { Scene } from "@babylonjs/core/scene";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../PlaceholderTag";

/** Original regulator in its native facing, grounded from audited source bounds.
 * Its existing logical handles and source-authored plumbing remain the fallback.
 */
export async function loadConservatoryRegulator(
  scene: Scene, shadows: ShadowGenerator,
): Promise<InstantiatedModel[]> {
  let instance: InstantiatedModel | null = null;
  let mount: TransformNode | null = null;
  try {
    instance = await getAssetManager().instantiateModel(
      scene, "/assets/models/environment/conservatory_mist_regulator.glb",
    );
    if (scene.isDisposed || !instance.meshes.some(m=>m.getTotalVertices()>0)) {
      instance.dispose(); return [];
    }
    const scale = 2.8;
    // Source-audit.json: original glTF minimum Y=-.9500930309295654.
    mount = new TransformNode("conservatoryRegulatorMount", scene);
    mount.position.set(22, .9500930309295654 * scale, -3.5);
    mount.scaling.setAll(scale);
    for (const root of instance.rootNodes) root.parent = mount;
    const geometry = instance.meshes.filter(m=>m.getTotalVertices()>0);
    for (const mesh of geometry) {
      mesh.isPickable = false; mesh.receiveShadows = true;
      shadows.addShadowCaster(mesh); tagAs(mesh,"final");
    }
    const owned = instance, ownedMount = mount;
    let disposed = false;
    return [{ ...owned, rootNodes: [ownedMount], dispose() {
      if (disposed) return; disposed = true;
      for (const mesh of geometry) shadows.removeShadowCaster(mesh);
      owned.dispose(); ownedMount.dispose();
    } }];
  } catch (error) {
    if (instance) {
      for (const mesh of instance.meshes) shadows.removeShadowCaster(mesh);
      instance.dispose();
    }
    mount?.dispose();
    if (!scene.isDisposed) console.warn("[Conservatory] Regulator unavailable; keeping functional handles:",error);
    return [];
  }
}
